import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { useOrpc } from './useClient'

export type FaxTaskSummary = Awaited<
  ReturnType<OrpcClient['faxTaskSummaries']>
>['summaries'][number]
export type FaxTask = FaxTaskSummary['tasks'][number]

// The faxTaskSummaries contract caps its input at 100 ids (a sane server-side
// guard). The fax inbox scrolls infinitely and hands us every id it has
// accumulated, so past 100 a single request would fail validation and ALL badges
// would silently disappear. Chunk instead: one query per 100 ids, never one per
// row.
const MAX_IDS_PER_REQUEST = 100

/**
 * Bulk lookup for a page of faxes — never call this per row. Returns a map keyed
 * by DocumentReference id so callers can index directly. Correct for any list
 * length: the ids are chunked and the results merged into one map.
 */
export function useFaxTaskSummaries(documentReferenceIds: string[]) {
  const orpc = useOrpc()
  const ids = [...new Set(documentReferenceIds)].filter(Boolean).sort()

  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
    chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST))
  }

  const queries = useQueries({
    queries: chunks.map((chunk) =>
      orpc.faxTaskSummaries.queryOptions({ input: { documentReferenceIds: chunk } }),
    ),
  })

  const byDocumentReference = new Map<string, FaxTaskSummary>(
    queries.flatMap((q) =>
      (q.data?.summaries ?? []).map((s) => [s.documentReferenceId, s] as const),
    ),
  )

  return {
    byDocumentReference,
    // A chunk still in flight leaves its faxes unbadged rather than wrong, so
    // callers that care aggregate across every chunk.
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  }
}

export function useCreateFaxTask() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.createFaxTask.mutationOptions({
      onSuccess: () => {
        // Both the panel badge and the inbox indicator read the summaries query.
        // `.key()` (from GeneralUtils) is a partial-matching key that invalidates
        // every summaries query regardless of which document ids it was called
        // with. `.queryKey({ input })` (used in CareTaskSettingsModal.tsx) is the
        // full key for one specific input and requires `input` since this
        // contract's input is not optional — it's the wrong tool here.
        queryClient.invalidateQueries({ queryKey: orpc.faxTaskSummaries.key() })
      },
      onError: () => {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Failed to create task.',
        })
      },
    }),
  )
}

export type OutgoingFaxLinkedTask = Awaited<
  ReturnType<OrpcClient['outgoingFaxLinks']>
>['tasks'][number]

/** The care tasks a sent fax has been linked to (PD-1606). */
export function useOutgoingFaxLinks(messageId: string) {
  const orpc = useOrpc()
  return useQuery(orpc.outgoingFaxLinks.queryOptions({ input: { messageId } }))
}

// The two surfaces render from different queries — the sent-fax page from
// outgoingFaxLinks, the task drawer from its board — so every link write
// invalidates both, whichever side performed it. `.key()` is the
// partial-matching key: it clears the links query for every message id and the
// board query for every board, which is correct here and cheaper to reason
// about than tracking which ids a write touched.
function useOutgoingFaxLinkInvalidation() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: orpc.outgoingFaxLinks.key() })
    void queryClient.invalidateQueries({ queryKey: orpc.boardGet.key() })
  }
}

export function useLinkOutgoingFax() {
  const orpc = useOrpc()
  const invalidate = useOutgoingFaxLinkInvalidation()

  return useMutation(
    orpc.linkOutgoingFax.mutationOptions({
      onSuccess: invalidate,
      onError: () => {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Failed to link the fax to that task.',
        })
      },
    }),
  )
}

export function useUpdateOutgoingFaxLink() {
  const orpc = useOrpc()
  const invalidate = useOutgoingFaxLinkInvalidation()

  return useMutation(
    orpc.updateOutgoingFaxLink.mutationOptions({
      onSuccess: invalidate,
      onError: () => {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Failed to update the fax link.',
        })
      },
    }),
  )
}

export function useUnlinkOutgoingFax() {
  const orpc = useOrpc()
  const invalidate = useOutgoingFaxLinkInvalidation()

  return useMutation(
    orpc.unlinkOutgoingFax.mutationOptions({
      onSuccess: invalidate,
      onError: () => {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Failed to remove the fax link.',
        })
      },
    }),
  )
}
